/**
 * teamHelpers.js
 * 
 * Helper functions to handle team fetching and joining logic.
 */

import { collection, getDocs, updateDoc, doc, arrayUnion, onSnapshot } from "firebase/firestore";
import { db } from "../config/firebase";
import { Teams } from "../assets/DatabaseCollections";

/**
 * Fetches all teams in a given room.
 * @param {string} dbCollection - The main AuctionRooms collection name.
 * @param {string} roomID - The room ID to fetch teams from.
 * @returns {Array} - Array of team objects [{ id, name, members, budget, budgetLeft, movies }]
 */
export const fetchTeams = async (dbCollection, roomID) => {
    try {
        const teamsRef = collection(db, dbCollection, roomID, Teams);
        const teamsSnap = await getDocs(teamsRef);

        const teams = teamsSnap.docs.map((docSnap) => ({
            id: docSnap.id,
            ...docSnap.data(),
        }));

        return teams;
    } catch (error) {
        console.error("Error fetching teams:", error);
        return [];
    }
};

/**
 * Listens to all teams in a room, calling onChange with the latest teams on every update.
 * @param {string} dbCollection - The main AuctionRooms collection name.
 * @param {string} roomID - The room ID to listen to.
 * @param {Function} onChange - Called with an array of team objects.
 * @returns {Function} - Unsubscribe function.
 */
export const subscribeToTeams = (dbCollection, roomID, onChange) => {
    const teamsRef = collection(db, dbCollection, roomID, Teams);
    return onSnapshot(
        teamsRef,
        (teamsSnap) => onChange(teamsSnap.docs.map((docSnap) => ({ id: docSnap.id, ...docSnap.data() }))),
        (error) => console.error("Error listening to teams:", error)
    );
};

/**
 * Adds a user to a specific team.
 * @param {string} dbCollection - The main AuctionRooms collection name.
 * @param {string} roomID - The room ID.
 * @param {string} teamID - The Firestore ID of the team to join.
 * @param {object} user - The current user object.
 */
export const joinTeam = async (dbCollection, roomID, teamID, user) => {
    try {
        const teamRef = doc(db, dbCollection, roomID, Teams, teamID);
        await updateDoc(teamRef, {
            members: arrayUnion({
                userID      : user.userID || user.uid,
                displayName : user.displayName || "Anonymous",
            }),
        });
        return true;
    } catch (error) {
        console.error("Error joining team:", error);
        return false;
    }
};